import { useState, useEffect } from "react";
import type { NoticeBoardItem } from "../../types/noticeboard";
import type { PropertyRecord } from "../../types/portfolio";
import type { InvestorProfile, TeamContact } from "../../types/entities";
import { resolveRecipient } from "../../lib/resolveRecipient";
import { EMAIL_TEMPLATES } from "../../lib/emailTemplates";

interface EmailComposeModalProps {
  item: NoticeBoardItem;
  property?: PropertyRecord;
  profile: InvestorProfile;
  teamContacts: TeamContact[];
  onClose: () => void;
}

export function EmailComposeModal({ item, property, profile, teamContacts, onClose }: EmailComposeModalProps) {
  const recipient = item.emailAction
    ? resolveRecipient(item.emailAction.recipientRole, property, teamContacts)
    : null;

  const [toEmail, setToEmail] = useState(recipient?.email ?? "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    if (!item.emailAction) return;
    const tmpl = EMAIL_TEMPLATES[item.emailAction.templateId];
    if (!tmpl || !property) return;
    const draft = tmpl({
      property,
      profile,
      recipientName: recipient?.name ?? "[Recipient]",
    });
    setSubject(draft.subject);
    setBody(draft.body);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.emailAction?.templateId]);

  function handleSend() {
    if (!toEmail) return;
    const url = `mailto:${encodeURIComponent(toEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
    onClose();
  }

  const roleLabel = item.emailAction?.recipientRole === "pm" ? "property manager"
    : item.emailAction?.recipientRole === "mortgage-broker" ? "mortgage broker"
    : item.emailAction?.recipientRole ?? "contact";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-neutral-900">Email {roleLabel}</h3>

        {/* First-time hint */}
        <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
          AusProp prepares the draft. Send opens your email app (Gmail, Outlook, etc.) with the content pre-filled — you control the final send from your own account.
        </div>

        {/* Missing contact warning */}
        {!recipient && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            No {roleLabel} configured. Add one in <strong>Profile → My Team</strong>, or enter an email below.
          </div>
        )}

        {/* To field */}
        <div>
          <label className="text-xs font-medium text-neutral-500">To</label>
          <input
            type="email"
            className="mt-0.5 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            placeholder={`${roleLabel}@example.com`}
          />
          {recipient && (
            <div className="mt-0.5 text-[10px] text-neutral-400">{recipient.name} ({recipient.source === "property-manager" ? "from property" : "from My Team"})</div>
          )}
        </div>

        {/* Subject */}
        <div>
          <label className="text-xs font-medium text-neutral-500">Subject</label>
          <input
            className="mt-0.5 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        {/* Body */}
        <div>
          <label className="text-xs font-medium text-neutral-500">Message</label>
          <textarea
            className="mt-0.5 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            rows={10}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="rounded-lg border border-neutral-200 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50">Cancel</button>
          <button
            onClick={handleSend}
            disabled={!toEmail || !subject}
            className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-50"
          >
            Send →
          </button>
        </div>
      </div>
    </div>
  );
}
